import {Component, ElementRef, EventEmitter, forwardRef, HostListener, Inject, Input, Output, ViewChild} from '@angular/core';
import { SettingsService } from "@app/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";

@Component({
    selector: 'app-new-time-picker',
    templateUrl: './new-time-picker.component.html',
    styleUrls: ['./new-time-picker.component.css'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => NewTimePickerComponent),
            multi: true
        }
    ]
})

export class NewTimePickerComponent implements ControlValueAccessor {

    @Output() timeChange = new EventEmitter<string>();
    @ViewChild('hourInput') hourInput: ElementRef<HTMLInputElement>;
    @ViewChild('minuteInput') minuteInput: ElementRef<HTMLInputElement>;

    @Input() set time(value: string) {
        if (!value || typeof value !== 'string') {
            this.applyDefaultTime();
            return;
        } else {
            this.applyIncomingTime(value);
        }
    }
    @Input() showTitle: boolean = true;
    @Input() minTime: string = ''; // Minimum time filter (e.g., "09:00" or "09:00 AM")
    @Input() minuteSteps: number = 15;
    @Input() maxTime: string = '';
    @Input() allowTyping: boolean = true;
    hour = '00';
    minute = '00';
    meridian = 'AM';
    showMeridian: boolean = true;
    value: any;
    showDropdown = false;
    isTyping = false;

    // dropdownMinuteSteps = ['00', '15', '30', '45'];

    constructor(
        private eRef: ElementRef,
        @Inject(forwardRef(() => SettingsService)) private settings
    ) {
        const intlSettings = this.settings.getIntlSettings();
        this.showMeridian = parseInt(intlSettings['time']) === 0;
    }

    onChange = (_: any) => { };
    onTouched = () => { };

    writeValue(val: any): void {
        if (val) {
            this.applyIncomingTime(val);
        }
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    updateValue(newValue: any) {
        this.value = newValue;
        this.onChange(newValue);
        this.onTouched();
    }

    ngOnInit() {
        const intlSettings = this.settings.getIntlSettings();
        this.showMeridian = parseInt(intlSettings['time']) === 0;
    }

    get dropdownMinuteSteps(): string[] {
        const steps: string[] = [];
        for (let m = 0; m < 60; m += this.minuteSteps) {
            steps.push(m.toString().padStart(2, '0'));
        }
        return steps;
    }

    @HostListener('document:click', ['$event'])
    handleClickOutside(event: any) {
        if (!this.eRef.nativeElement.contains(event.target)) {
            this.showDropdown = false;
            this.isTyping = false;
            this.showMeridianDropdown = false;
        }
    }

    get meridianList(): string[] {
        if (!this.minTime && !this.maxTime) return ['AM', 'PM'];

        const validMeridians: string[] = [];

        for (const mer of ['AM', 'PM']) {
            const hourOrder = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
            const hasValidTime = hourOrder.some(h =>
                this.dropdownMinuteSteps.some(m => {
                    const minutes = this.parse12HourTimeToMinutes(h, parseInt(m), mer);
                    const validMin = !this.minTime || minutes >= this.parseTimeToMinutes(this.minTime);
                    const validMax = !this.maxTime || minutes <= this.parseTimeToMinutes(this.maxTime);
                    return validMin && validMax;
                })
            );
            if (hasValidTime) validMeridians.push(mer);
        }

        return validMeridians;
    }
    emitFinalValue() {
        const formatted =
            this.showMeridian
                ? `${this.hour}:${this.minute} ${this.meridian}`
                : `${this.hour}:${this.minute}`;

        this.timeChange.emit(formatted);
        this.updateValue(formatted);
    }

    get displayValue() {
        return this.showMeridian
            ? `${this.hour}:${this.minute} ${this.meridian}`
            : `${this.hour}:${this.minute}`;
    }

    applyIncomingTime(value: string) {
        value = value.trim();

        const isIncoming12Hr = /^(0?\d|1[0-2]):[0-5]\d\s?(AM|PM)$/i.test(value);
        const isIncoming24Hr = /^([01]?\d|2[0-3]):[0-5]\d$/.test(value);

        const isComponent12Hr = this.showMeridian;

        if (!isIncoming12Hr && !isIncoming24Hr) {
            this.applyDefaultTime();
            return;
        }

        if (isIncoming12Hr && !isComponent12Hr) {
            value = this.convert12To24(value);
        }

        if (!isIncoming12Hr && isComponent12Hr) {
            value = this.convert24To12(value);
        }

        this.setInitialTime(value);
    }

    setInitialTime(value: string) {
        if (this.showMeridian) {
            const match = value.match(/^(\d{1,2}):(\d{2})\s?(AM|PM)$/i);
            if (match) {
                this.hour = match[1].padStart(2, '0');
                this.minute = match[2].padStart(2, '0');
                this.meridian = match[3].toUpperCase();
            }
        } else {
            const match = value.match(/^(\d{1,2}):(\d{2})$/);
            if (match) {
                this.hour = match[1].padStart(2, '0');
                this.minute = match[2].padStart(2, '0');
            }
        }
    }

    convert12To24(value: string): string {
        const match = value.match(/^(\d{1,2}):(\d{2})\s?(AM|PM)$/i);
        if (!match) return value;

        let hour = parseInt(match[1], 10);
        const minute = match[2];
        const mer = match[3].toUpperCase();

        if (mer === 'PM' && hour !== 12) hour += 12;
        if (mer === 'AM' && hour === 12) hour = 0;

        return `${hour.toString().padStart(2, '0')}:${minute}`;
    }

    convert24To12(value: string): string {
        const match = value.match(/^(\d{1,2}):(\d{2})$/);
        if (!match) return value;

        let hour = parseInt(match[1], 10);
        const minute = match[2];
        let mer = 'AM';

        if (hour >= 12) {
            mer = 'PM';
            if (hour > 12) hour -= 12;
        } else if (hour === 0) {
            hour = 12;
        }

        return `${hour.toString().padStart(2, '0')}:${minute} ${mer}`;
    }

    applyDefaultTime() {
        this.hour = '00';
        this.minute = '00';
        if (this.showMeridian) {
            this.meridian = 'AM';
            this.hour = '12';
        }
        this.emitFinalValue();
    }

    get timeList(): string[] {
        const list: string[] = [];
        const exist = new Set<string>();
        let minTimeInMinutes = -1;
        let maxTimeInMinutes = -1;

        if (this.minTime) minTimeInMinutes = this.parseTimeToMinutes(this.minTime);
        if (this.maxTime) maxTimeInMinutes = this.parseTimeToMinutes(this.maxTime);

        if (this.showMeridian) {
            const hourOrder = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
            for (const meridian of ['AM', 'PM']) {
                for (const h of hourOrder) {
                    for (const m of this.dropdownMinuteSteps) {
                        const currentTimeInMinutes = this.parse12HourTimeToMinutes(h, parseInt(m), meridian);

                        if (minTimeInMinutes >= 0 && currentTimeInMinutes < minTimeInMinutes) continue;
                        if (maxTimeInMinutes >= 0 && currentTimeInMinutes > maxTimeInMinutes) continue;

                        const timeStr = `${h.toString().padStart(2, '0')}:${m}`;

                        if (exist.has(timeStr)) continue;
                        exist.add(timeStr);
                        list.push(timeStr);
                    }
                }
            }
        } else {
            for (let h = 0; h < 24; h++) {
                for (const m of this.dropdownMinuteSteps) {
                    const currentTimeInMinutes = h * 60 + parseInt(m);

                    if (minTimeInMinutes >= 0 && currentTimeInMinutes < minTimeInMinutes) continue;
                    if (maxTimeInMinutes >= 0 && currentTimeInMinutes > maxTimeInMinutes) continue;

                    list.push(`${h.toString().padStart(2, '0')}:${m}`);
                }
            }
        }

        return list;
    }
    /**
     * Parse time string to minutes since midnight
     */
    parseTimeToMinutes(timeStr: string): number {
        if (!timeStr) return -1;

        timeStr = timeStr.trim();

        // Check for 12-hour format with AM/PM
        const match12 = timeStr.match(/^(\d{1,2}):(\d{2})\s?(AM|PM)$/i);
        if (match12) {
            let hour = parseInt(match12[1], 10);
            const minute = parseInt(match12[2], 10);
            const meridian = match12[3].toUpperCase();

            return this.parse12HourTimeToMinutes(hour, minute, meridian);
        }

        // Check for 24-hour format
        const match24 = timeStr.match(/^(\d{1,2}):(\d{2})$/);
        if (match24) {
            const hour = parseInt(match24[1], 10);
            const minute = parseInt(match24[2], 10);
            return hour * 60 + minute;
        }

        return -1;
    }

    /**
     * Convert 12-hour time to minutes since midnight
     */
    parse12HourTimeToMinutes(hour: number, minute: number, meridian: string): number {
        if (meridian === 'PM' && hour !== 12) {
            hour += 12;
        } else if (meridian === 'AM' && hour === 12) {
            hour = 0;
        }
        return hour * 60 + minute;
    }


    openDropdown() {
        // Don't open dropdown if user is actively typing
        if (this.isTyping) return;

        this.showDropdown = true;
        this.showMeridianDropdown = false;

        // Auto-scroll to selected time after dropdown renders
        setTimeout(() => {
            this.scrollToSelectedTime();
        }, 50); // Small delay to ensure DOM is rendered
    }

    /**
     * Handle focus on hour input - select all text for easy editing
     */
    onHourFocus(event: FocusEvent) {
        const input = event.target as HTMLInputElement;
        setTimeout(() => {
            input.select();
        }, 0);
    }

    /**
     * Handle click on hour input
     */
    onHourClick() {
        // Don't interfere with typing
        if (this.isTyping) return;

        this.openDropdown();
    }

    /**
     * Handle focus on minute input - select all text for easy editing
     */
    onMinuteFocus(event: FocusEvent) {
        const input = event.target as HTMLInputElement;
        setTimeout(() => {
            input.select();
        }, 0);
    }

    /**
     * Handle click on minute input
     */
    onMinuteClick() {
        // Don't interfere with typing
        if (this.isTyping) return;

        this.openDropdown();
    }

    /**
     * Scroll dropdown to show the currently selected time
     */
    scrollToSelectedTime() {
        const dropdownElement = this.eRef.nativeElement.querySelector('.dropdown-values');
        if (!dropdownElement) return;

        const currentTime = `${this.hour}:${this.minute}`;
        const items = dropdownElement.querySelectorAll('.dropdown-item');

        for (let i = 0; i < items.length; i++) {
            const item = items[i] as HTMLElement;
            const itemText = item.textContent ? item.textContent.trim() : '';
            if (itemText === currentTime) {
                // Calculate scroll position to center the item
                const itemHeight = item.offsetHeight;
                const dropdownHeight = dropdownElement.clientHeight;
                const scrollTop = item.offsetTop - (dropdownHeight / 2) + (itemHeight / 2);

                // Scroll to the calculated position
                dropdownElement.scrollTop = scrollTop;
                break;
            }
        }
    }

    chooseTime(value: string) {
        const [h, m] = value.split(':');
        this.hour = h;
        this.minute = m;
        this.showDropdown = false;
        this.checkAndSnapRange();
    }

    onHourInput(event: Event) {
        this.isTyping = true;
        this.showDropdown = false;

        const input = event.target as HTMLInputElement;
        let value = input.value.replace(/\D/g, ''); // Remove non-digits

        // If the input was cleared, allow typing
        if (value.length === 0) {
            this.hour = '';
            return;
        }

        // Validate and limit hour input
        if (this.showMeridian) {
            // 12-hour format: 01-12
            if (value.length === 1) {
                const digit = parseInt(value);
                if (digit > 1) {
                    value = '0' + digit;
                    this.hour = value;
                    this.focusMinuteInput();
                    return;
                }
            } else if (value.length >= 2) {
                value = value.substring(0, 2);
                let num = parseInt(value);
                if (num === 0) num = 12;
                else if (num > 12) num = 12;
                value = num.toString().padStart(2, '0');
                this.hour = value;
                this.focusMinuteInput();
                return;
            }
        } else {
            // 24-hour format: 00-23
            if (value.length === 1) {
                const digit = parseInt(value);
                if (digit > 2) {
                    value = '0' + digit;
                    this.hour = value;
                    this.focusMinuteInput();
                    return;
                }
            } else if (value.length >= 2) {
                value = value.substring(0, 2);
                let num = parseInt(value);
                if (num > 23) num = 23;
                value = num.toString().padStart(2, '0');
                this.hour = value;
                this.focusMinuteInput();
                return;
            }
        }

        this.hour = value;
        if (this.hour.length === 2 && this.minute.length === 2) {
           this.emitFinalValue();
        }

    }

    onMinuteInput(event: Event) {
        this.isTyping = true;
        this.showDropdown = false;

        const input = event.target as HTMLInputElement;
        let value = input.value.replace(/\D/g, ''); // Remove non-digits

        // If the input was cleared, allow typing
        if (value.length === 0) {
            this.minute = '';
            return;
        }

        // Validate and limit minute input
        if (value.length === 1) {
            const digit = parseInt(value);
            if (digit > 5) {
                value = '0' + digit;
                this.minute = value;
                return;
            }
        } else if (value.length >= 2) {
            value = value.substring(0, 2);
            let num = parseInt(value);
            if (num > 59) num = 59;
            value = num.toString().padStart(2, '0');
        }

        this.minute = value;
        if (this.hour.length === 2 && this.minute.length === 2) {
           this.emitFinalValue();
        }

    }

    isWithinTimeRange(): boolean {
        if (!this.minTime && !this.maxTime) return true;

        const h = parseInt(this.hour, 10);
        const m = parseInt(this.minute, 10);
        const currentMinutes = this.showMeridian
            ? this.parse12HourTimeToMinutes(h, m, this.meridian)
            : h * 60 + m;

        if (this.minTime) {
            const minMinutes = this.parseTimeToMinutes(this.minTime);
            if (currentMinutes < minMinutes) return false;
        }

        if (this.maxTime) {
            const maxMinutes = this.parseTimeToMinutes(this.maxTime);
            if (currentMinutes > maxMinutes) return false;
        }

        return true;
    }

    checkAndSnapRange() {
        if (!this.minTime && !this.maxTime) {
            this.emitFinalValue();
            return;
        }

        if (!this.isWithinTimeRange()) {
            this.snapToNearestValidTime();
        } else {
            this.emitFinalValue();
        }
    }

    snapToNearestValidTime() {
        const h = parseInt(this.hour, 10);
        const m = parseInt(this.minute, 10);

        const minMinutes = this.minTime ? this.parseTimeToMinutes(this.minTime) : 0;
        const maxMinutes = this.maxTime ? this.parseTimeToMinutes(this.maxTime) : 24 * 60;

        if (this.showMeridian) {
            const oppositeMeridian = this.meridian === 'AM' ? 'PM' : 'AM';
            const oppositeMinutes = this.parse12HourTimeToMinutes(h, m, oppositeMeridian);

            if (oppositeMinutes >= minMinutes && oppositeMinutes <= maxMinutes) {
                this.meridian = oppositeMeridian;
                this.emitFinalValue();
                return;
            }
        }

        const currentMinutes = this.showMeridian
            ? this.parse12HourTimeToMinutes(h, m, this.meridian)
            : h * 60 + m;

        if (currentMinutes < minMinutes) {
            this.applyIncomingTime(this.minTime);
            this.emitFinalValue();
            return;
        }

        if (currentMinutes > maxMinutes) {
            this.applyIncomingTime(this.maxTime);
            this.emitFinalValue();
            return;
        }

        this.emitFinalValue();
    }

    onHourKeydown(event: KeyboardEvent) {
        const input = event.target as HTMLInputElement;

        // If user types a digit and text is selected, clear and start fresh
        if (/^\d$/.test(event.key)) {
            const hasSelection = input.selectionStart !== input.selectionEnd;
            if (hasSelection) {
                event.preventDefault();
                this.hour = event.key;
                input.value = event.key;
                // Trigger input event manually
                const inputEvent = new Event('input', { bubbles: true });
                input.dispatchEvent(inputEvent);
                return;
            }
        }

        // Handle backspace
        if (event.key === 'Backspace') {
            if (input.value.length === 0) {
                event.preventDefault();
                return;
            }
            return;
        }

        // Handle delete key
        if (event.key === 'Delete') {
            return;
        }

        // Handle arrow right - move to minutes
        if (event.key === 'ArrowRight' && input.selectionStart === input.value.length) {
            event.preventDefault();
            this.focusMinuteInput();
            return;
        }

        // Handle colon - move to minutes
        if (event.key === ':') {
            event.preventDefault();
            this.focusMinuteInput();
            return;
        }

        // Only allow numeric input
        if (!/^\d$/.test(event.key) && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(event.key)) {
            event.preventDefault();
        }
    }

    onMinuteKeydown(event: KeyboardEvent) {
        const input = event.target as HTMLInputElement;

        // If user types a digit and text is selected, clear and start fresh
        if (/^\d$/.test(event.key)) {
            const hasSelection = input.selectionStart !== input.selectionEnd;
            if (hasSelection) {
                event.preventDefault();
                this.minute = event.key;
                input.value = event.key;
                // Trigger input event manually
                const inputEvent = new Event('input', { bubbles: true });
                input.dispatchEvent(inputEvent);
                return;
            }
        }

        // Handle backspace - smart deletion flow
        if (event.key === 'Backspace') {
            const cursorPosition = input.selectionStart;
            const hasSelection = input.selectionStart !== input.selectionEnd;
            const valueLength = input.value.length;

            // If all text is selected, clear and stay in minute field
            if (hasSelection && input.selectionStart === 0 && input.selectionEnd === valueLength) {
                this.minute = '';
                event.preventDefault();
                return;
            }

            // If cursor is at position 0 (start of field) and no selection
            if (cursorPosition === 0 && !hasSelection) {
                event.preventDefault();

                // If minute has content, just move to hour without deleting
                if (valueLength > 0) {
                    this.focusHourInput();
                    return;
                }

                // If minute is empty, delete last character from hour and move focus
                if (this.hour.length > 0) {
                    this.hour = this.hour.slice(0, -1);
                }
                this.focusHourInput();
                return;
            }

            return;
        }

        // Handle delete key
        if (event.key === 'Delete') {
            if (input.selectionStart === 0 && input.selectionEnd === input.value.length) {
                this.minute = '';
                event.preventDefault();
            }
            return;
        }

        // Handle arrow left - move to hour if at start
        if (event.key === 'ArrowLeft' && input.selectionStart === 0) {
            event.preventDefault();
            this.focusHourInput();
            return;
        }

        // Only allow numeric input
        if (!/^\d$/.test(event.key) && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(event.key)) {
            event.preventDefault();
        }
    }

    focusHourInput() {
        if (this.hourInput) {
            setTimeout(() => {
                this.hourInput.nativeElement.focus();
                this.hourInput.nativeElement.setSelectionRange(
                    this.hourInput.nativeElement.value.length,
                    this.hourInput.nativeElement.value.length
                );
            }, 0);
        }
    }

    focusMinuteInput() {
        if (this.minuteInput) {
            setTimeout(() => {
                this.minuteInput.nativeElement.focus();
                // Select all text in minute input so typing replaces it
                this.minuteInput.nativeElement.select();
            }, 0);
        }
    }

    onBlur() {
        this.normalizeTime();
        this.isTyping = false;
    }

    normalizeTime() {
        let h = parseInt(this.hour || '0', 10);
        let m = parseInt(this.minute || '0', 10);

        if (this.showMeridian) {
            if (h === 0) h = 12;
            h = Math.min(Math.max(h, 1), 12);
        } else {
            h = Math.min(Math.max(h, 0), 23);
        }

        m = Math.min(Math.max(m, 0), 59);
        this.hour = h.toString().padStart(2, '0');
        this.minute = m.toString().padStart(2, '0');

        this.checkAndSnapRange();
    }

    onMeridianOpen() {
        this.showDropdown = false;
        this.isTyping = false;
    }

    showMeridianDropdown = false;

    toggleMeridianDropdown() {
        this.showDropdown = false;
        this.showMeridianDropdown = !this.showMeridianDropdown;
    }
    selectMeridian(value: string) {
        this.meridian = value;
        this.showMeridianDropdown = false;
        this.checkAndSnapRange();
    }
}
